import React, { useState, useEffect, useCallback } from 'react';
import {
  ShoppingBag,
  FileCheck,
  MapPin,
  Settings as SettingsIcon,
  CreditCard,
  CheckCircle2,
  AlertTriangle,
  Menu,
  X,
} from 'lucide-react';
import OrdersList from './components/OrdersList';
import CreateLabelModal from './components/CreateLabelModal';
import LabelsArchive from './components/LabelsArchive';
import LabelPreviewModal from './components/LabelPreviewModal';
import DeliveryPoints from './components/DeliveryPoints';
import Settings from './components/Settings';
import Billing from './components/Billing';
import { api, hasAppBridge, openPricingPlans } from './lib/api';

// One source of truth for the main navigation: rendered as top tabs on desktop
// and as the slide-in sidebar on mobile.
const TABS = [
  { id: 'orders', label: 'Orders & Labels', Icon: ShoppingBag },
  { id: 'archive', label: 'Labels Archive', Icon: FileCheck },
  { id: 'delivery_points', label: 'HP Delivery Points', Icon: MapPin },
  { id: 'billing', label: 'Plan & Usage', Icon: CreditCard },
  { id: 'settings', label: 'Plugin Settings', Icon: SettingsIcon },
];

export default function App() {
  const [activeTab, setActiveTab] = useState('orders');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Shop context, replacing the hardcoded "moj-shop.myshopify.com" / "TEST" chips.
  const [shopContext, setShopContext] = useState(null);
  const [bootError, setBootError] = useState(null);

  // Which order the create-label modal is open for, and in which direction.
  const [labelModal, setLabelModal] = useState({ order: null, direction: 'RETURN' });
  const [previewModal, setPreviewModal] = useState({ isOpen: false, labelData: null, orderName: '' });
  const [toast, setToast] = useState(null);
  const [ordersReloadKey, setOrdersReloadKey] = useState(0);

  const loadShopContext = useCallback(async () => {
    try {
      const data = await api.getShop();
      setShopContext({
        shop: data.shop,
        hpEnv: data.hpEnv,
        configured: data.configured,
        billing: data.billing,
      });
      setBootError(null);
    } catch (err) {
      setBootError(err.message);
    }
  }, []);

  useEffect(() => {
    loadShopContext();
  }, [loadShopContext]);

  // While the mobile sidebar is open: trap the page behind it, close on Escape,
  // and close if the viewport grows past the breakpoint where the drawer hides.
  useEffect(() => {
    if (!sidebarOpen) return undefined;

    const closeSidebar = () => setSidebarOpen(false);
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeSidebar();
    };
    const desktopQuery = window.matchMedia('(min-width: 769px)');
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    desktopQuery.addEventListener('change', closeSidebar);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
      desktopQuery.removeEventListener('change', closeSidebar);
    };
  }, [sidebarOpen]);

  const selectTab = (tabId) => {
    setActiveTab(tabId);
    setSidebarOpen(false);
  };

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 5000);
  };

  const handleLabelCreated = (label, orderName, usage, direction) => {
    setOrdersReloadKey((k) => k + 1);
    // The create response carries the new usage, so the header counter and the
    // quota banner update without a second call to /api/shop.
    if (usage) {
      setShopContext((prev) => (prev?.billing ? { ...prev, billing: { ...prev.billing, usage } } : prev));
    }
    const noun = direction === 'OUTBOUND' ? 'Shipping label' : 'Return label';
    showToast(`${noun} created for ${orderName}. Barcode ${label.barcode}`);
    setPreviewModal({ isOpen: true, labelData: label, orderName });
  };

  const handleViewLabel = (labelData, orderName) => {
    setPreviewModal({ isOpen: true, labelData, orderName });
  };

  // Without App Bridge there is no session token, so every API call would 401.
  // Say so plainly rather than showing an app full of failed requests.
  if (!hasAppBridge() && bootError) {
    return (
      <div style={{ maxWidth: '560px', margin: '4rem auto', padding: '0 1.5rem', textAlign: 'center' }}>
        <AlertTriangle size={40} color="#b98900" style={{ marginBottom: '1rem' }} />
        <h1 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>
          Open this app from your Shopify admin
        </h1>
        <p style={{ color: 'var(--color-secondary)', fontSize: '0.9rem' }}>
          The HP Return Label app runs embedded in Shopify and needs a session from the admin to
          load your orders. Go to <strong>Apps &rarr; HP Return Label</strong> in your store admin.
        </p>
        <p style={{ color: 'var(--color-secondary)', fontSize: '0.8rem', marginTop: '1rem' }}>{bootError}</p>
      </div>
    );
  }

  const needsSetup = shopContext && !shopContext.configured;

  const activeTabDef = TABS.find((tab) => tab.id === activeTab) ?? TABS[0];
  const ActiveTabIcon = activeTabDef.Icon;

  const billing = shopContext?.billing;
  const usage = billing?.usage;
  // Nudge at 80% of the allowance, hard-stop banner once it is gone.
  const atLimit = Boolean(usage && !usage.unlimited && usage.remaining === 0);
  const nearLimit = Boolean(
    usage && !usage.unlimited && !atLimit && usage.limit && usage.used / usage.limit >= 0.8
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header className="app-header">
        <div className="brand-container">
          <div className="hp-badge-logo">HP PAKET24</div>
          <div>
            <h1 className="app-title">Hrvatska Pošta Label Generator</h1>
            <p style={{ fontSize: '0.78rem', color: 'var(--color-secondary)' }}>
              Shipping and return labels for Shopify orders via HP DXWebAPI
            </p>
          </div>
        </div>

        <div className="store-info">
          {shopContext && (
            <>
              <span className="api-status-tag">
                <span
                  className="api-status-dot"
                  style={{ background: shopContext.configured ? undefined : '#b98900' }}
                />{' '}
                {shopContext.configured
                  ? `HP DXWebAPI configured (${shopContext.hpEnv})`
                  : 'HP DXWebAPI not configured'}
              </span>
              {billing && (
                <button
                  type="button"
                  onClick={() => selectTab('billing')}
                  title="View plan and usage"
                  style={{
                    background: atLimit ? '#fce8e6' : '#f1f2f3',
                    color: atLimit ? '#c5221f' : 'var(--color-primary)',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.35rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.82rem',
                    fontWeight: 600,
                  }}
                >
                  {billing.plan.name}
                  {usage.unlimited ? '' : ` · ${usage.used}/${usage.limit} labels`}
                </button>
              )}
              <div
                style={{
                  background: '#f1f2f3',
                  padding: '0.35rem 0.75rem',
                  borderRadius: '6px',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                }}
              >
                {shopContext.shop}
              </div>
            </>
          )}
        </div>
      </header>

      <main className="app-container" style={{ flex: 1 }}>
        {needsSetup && activeTab !== 'settings' && (
          <div
            style={{
              background: '#fff8e1',
              border: '1px solid #f0d48a',
              color: '#6b4e00',
              padding: '0.85rem 1.15rem',
              borderRadius: '8px',
              marginBottom: '1.25rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              fontSize: '0.88rem',
            }}
          >
            <AlertTriangle size={18} />
            <span style={{ flex: 1 }}>
              Finish setup before creating labels: HP DXWebAPI credentials and your return warehouse
              address are required.
            </span>
            <button className="btn btn-secondary btn-sm" onClick={() => selectTab('settings')}>
              Open settings
            </button>
          </div>
        )}

        {(atLimit || nearLimit) && activeTab !== 'billing' && (
          <div
            style={{
              background: atLimit ? '#fce8e6' : '#fff8e1',
              border: `1px solid ${atLimit ? '#f3b8b4' : '#f0d48a'}`,
              color: atLimit ? '#c5221f' : '#6b4e00',
              padding: '0.85rem 1.15rem',
              borderRadius: '8px',
              marginBottom: '1.25rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              fontSize: '0.88rem',
            }}
          >
            <AlertTriangle size={18} />
            <span style={{ flex: 1 }}>
              {atLimit
                ? `You have used all ${usage.limit} labels in your ${billing.plan.name} plan this month. Upgrade to create more.`
                : `${usage.remaining} of ${usage.limit} labels left on your ${billing.plan.name} plan this month.`}
            </span>
            <button className="btn btn-primary btn-sm" onClick={() => openPricingPlans(billing.upgradeUrl)}>
              <CreditCard size={14} /> Upgrade
            </button>
          </div>
        )}

        <div className="mobile-nav-bar">
          <button
            type="button"
            className="mobile-nav-toggle"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open navigation menu"
            aria-expanded={sidebarOpen}
            aria-controls="app-sidebar"
          >
            <Menu size={20} />
          </button>
          <span className="mobile-nav-current">
            <ActiveTabIcon size={16} /> {activeTabDef.label}
          </span>
        </div>

        <nav className="nav-tabs">
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              className={`nav-tab ${activeTab === id ? 'active' : ''}`}
              onClick={() => selectTab(id)}
            >
              <Icon size={18} /> {label}
            </button>
          ))}
        </nav>

        {toast && (
          <div
            style={{
              position: 'fixed',
              bottom: '1.5rem',
              right: '1.5rem',
              background: '#0d522c',
              color: 'white',
              padding: '0.85rem 1.25rem',
              borderRadius: '8px',
              boxShadow: '0 10px 15px -3px rgba(0,0,0,0.2)',
              zIndex: 999,
              display: 'flex',
              alignItems: 'center',
              gap: '0.6rem',
              fontSize: '0.9rem',
              fontWeight: 500,
            }}
          >
            <CheckCircle2 size={18} />
            <span>{toast}</span>
          </div>
        )}

        {activeTab === 'orders' && (
          <OrdersList
            reloadKey={ordersReloadKey}
            onCreateLabel={(order, direction) => setLabelModal({ order, direction })}
            onViewLabel={handleViewLabel}
          />
        )}

        {activeTab === 'archive' && <LabelsArchive onViewLabel={handleViewLabel} />}

        {activeTab === 'delivery_points' && <DeliveryPoints />}

        {activeTab === 'billing' && <Billing onPlanChanged={loadShopContext} />}

        {activeTab === 'settings' && <Settings onSaved={loadShopContext} />}
      </main>

      {/* Mobile navigation drawer: hidden by CSS above the 768px breakpoint. */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />
      <aside
        id="app-sidebar"
        className={`mobile-sidebar ${sidebarOpen ? 'open' : ''}`}
        aria-hidden={!sidebarOpen}
      >
        <div className="mobile-sidebar-header">
          <div className="hp-badge-logo">HP PAKET24</div>
          <button
            type="button"
            className="mobile-sidebar-close"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close navigation menu"
          >
            <X size={20} />
          </button>
        </div>
        <nav className="sidebar-nav">
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              className={`sidebar-link ${activeTab === id ? 'active' : ''}`}
              onClick={() => selectTab(id)}
              tabIndex={sidebarOpen ? 0 : -1}
            >
              <Icon size={18} /> {label}
            </button>
          ))}
        </nav>
      </aside>

      <CreateLabelModal
        isOpen={Boolean(labelModal.order)}
        onClose={() => setLabelModal({ order: null, direction: 'RETURN' })}
        order={labelModal.order}
        direction={labelModal.direction}
        onLabelCreated={handleLabelCreated}
      />

      <LabelPreviewModal
        isOpen={previewModal.isOpen}
        onClose={() => setPreviewModal({ isOpen: false, labelData: null, orderName: '' })}
        labelData={previewModal.labelData}
        orderName={previewModal.orderName}
      />
    </div>
  );
}
